import React, { useState, useEffect } from "react";
import { IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { FormGroup, Input } from "reactstrap";
import { Owner, Rider, Trainer } from "../../../models";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import ErrorAlert from "../../Errors/ErrorAlert";

interface _Props {
    requiredFields?: string[];
    rider?: Rider | null;
    secondRider?: Rider | null;
    owner?: Owner | null;
    trainer?: Trainer | null;
    selectedPayerName?: string | null;
    onSelectPayerPerson: (personName?: string, areAllRequiredFieldsComplete?: Boolean) => void;
    onFieldChange?: (payerName?: string, areAllRequiredFieldsComplete?: Boolean) => void;
}
interface PayerFormState {
    payerType: string;
    name: string;
}

const SelectPayerSection: React.FC<_Props> = ({
    requiredFields,
    rider,
    secondRider,
    owner,
    trainer,
    selectedPayerName,
    onSelectPayerPerson,
    onFieldChange
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // selectedPayerState triggers re-renders and allows us to bind data to the UI
    const [formState, setFormState] = useState<PayerFormState>({
        payerType: "",
        name: ""
    });

    // Track previous values to detect which section changed
    const [prevRider, setPrevRider] = useState<Rider | null | undefined>(rider);
    const [prevSecondRider, setPrevSecondRider] = useState<Rider | null | undefined>(secondRider);
    const [prevOwner, setPrevOwner] = useState<Owner | null | undefined>(owner);
    const [prevTrainer, setPrevTrainer] = useState<Trainer | null | undefined>(trainer);

    // If a payerName is passed in, update the form
    useEffect(() => {
       if (selectedPayerName !== formState.name) checkFormState();
    }, [selectedPayerName])

    useEffect(() => {
        // Check if the currently selected payer type's corresponding section was changed
        const currentPayerType = formState.payerType;
        let shouldClearPayerType = false;

        // Only clear if the currently selected payer type's section was changed
        if (currentPayerType === "owner" && owner && prevOwner && owner.id !== prevOwner.id) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "owner" && !owner && prevOwner) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "rider" && rider && prevRider && rider.id !== prevRider.id) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "rider" && !rider && prevRider) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "secondRider" && secondRider && prevSecondRider && secondRider.id !== prevSecondRider.id) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "secondRider" && !secondRider && prevSecondRider) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "trainer" && trainer && prevTrainer && trainer.id !== prevTrainer.id) {
            shouldClearPayerType = true;
        } else if (currentPayerType === "trainer" && !trainer && prevTrainer) {
            shouldClearPayerType = true;
        }

        if (shouldClearPayerType) {
            const emptyFormState: PayerFormState = {
                payerType: "-",
                name: ""
            };
            setFormState(emptyFormState);
            onFieldChange?.(undefined, false);
            onSelectPayerPerson(undefined, false);
        }

        setPrevOwner(owner);
        setPrevRider(rider);
        setPrevSecondRider(secondRider);
        setPrevTrainer(trainer);
        
    }, [rider, secondRider, owner, trainer, formState.payerType]);

    const checkFormState = () => {
        let updatedFormState: PayerFormState = {
            payerType: "-",
            name: ""
        };

        if (selectedPayerName && selectedPayerName !== "") {
            const cleanedPayerName = selectedPayerName.toLocaleLowerCase().trim();

            if (cleanedPayerName === rider?.name.toLocaleLowerCase().trim()) {
                updatedFormState = {
                    payerType: "rider",
                    name: selectedPayerName
                };
            } else if (cleanedPayerName === secondRider?.name.toLocaleLowerCase().trim()) {
                updatedFormState = {
                    payerType: "secondRider",
                    name: selectedPayerName
                };
            } else if (cleanedPayerName === owner?.name.toLocaleLowerCase().trim()) {
                updatedFormState = {
                    payerType: "owner",
                    name: selectedPayerName
                };
            } else if (cleanedPayerName === trainer?.name.toLocaleLowerCase().trim()) {
                updatedFormState = {
                    payerType: "trainer",
                    name: selectedPayerName
                };
            }
        }
        setFormState(updatedFormState);
        handleFieldChange(updatedFormState);
        const isSectionValid = checkIfSectionIsValid(updatedFormState);
        onSelectPayerPerson(updatedFormState.name === "" ? undefined : updatedFormState.name, isSectionValid);
    }

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex((field) => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    };

    const checkIfSectionIsValid = (currentFormState: PayerFormState) => {
        const missingFields: string[] = [];
        if (checkIfFieldIsRequired("type") && (currentFormState.payerType === "" || currentFormState.payerType === "-")) missingFields.push("type");
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") missingFields.push("name");
        const isValid = missingFields.length === 0;
        return isValid;
    };

    const handleFieldChange = (updatedFormState: PayerFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange(updatedFormState.name, isSectionValid);
        }
    };

    const handlePayerTypeChange = async (newPayerType: string) => {
        setIsLoading(true);
        setError("");

        let updatedFormState: PayerFormState = {
            payerType: newPayerType,
            name: ""
        };

        if (newPayerType === "owner") {
            updatedFormState = {
                payerType: newPayerType,
                name: owner?.name || ""
            };
        } else if (newPayerType === "rider") {
            updatedFormState = {
                payerType: newPayerType,
                name: rider?.name || ""
            };
        } else if (newPayerType === "secondRider") {
            updatedFormState = {
                payerType: newPayerType,
                name: secondRider?.name || ""
            };
        } else if (newPayerType === "trainer") {
            updatedFormState = {
                payerType: newPayerType,
                name: trainer?.name || ""
            };
        }

        setFormState(updatedFormState);
        handleFieldChange(updatedFormState);
        const isSectionValid = checkIfSectionIsValid(updatedFormState);
        onSelectPayerPerson(updatedFormState.name === "" ? undefined : updatedFormState.name, isSectionValid);
        setIsLoading(false);
    };

    const handleNameChange = (newName: string) => {
        setError("");
        const updatedFormState: PayerFormState = {
            payerType: formState.payerType,
            name: newName
        };
        setFormState((prev) => ({
            ...prev,
            name: newName,
        }));
        handleFieldChange(updatedFormState);

        const isSectionValid = checkIfSectionIsValid(updatedFormState);
        onSelectPayerPerson(updatedFormState.name === "" ? undefined : updatedFormState.name, isSectionValid);
    };

    return (
        <>
            <div data-section="payer">
                {error && <ErrorAlert error={error} width="12" />}
                <IonRow>
                    <IonCol size="12">
                        <FormGroup>
                            <IonLabel position="stacked">
                                Primary Payer
                                {checkIfFieldIsRequired("type") && <RequiredInputIndicator />}
                            </IonLabel>
                            <Input
                                name="payerType"
                                type="select"
                                value={formState.payerType}
                                onChange={(e: any) => {
                                    handlePayerTypeChange(e.target.value);
                                }}>
                                <option>-</option>
                                <option value="owner">{owner?.name ? `Owner - ${owner.name}` : "Owner"}</option>
                                <option value="rider">{rider?.name ? `Rider - ${rider.name}` : "Rider"}</option>
                                {secondRider && <option value="secondRider">{secondRider?.name ? `Second Rider - ${secondRider.name}` : "Second Rider"}</option>}
                                <option value="trainer">{trainer?.name ? `Trainer - ${trainer.name}` : "Trainer"}</option>
                                <option value="other">Other</option>
                            </Input>
                        </FormGroup>
                    </IonCol>
                </IonRow>
            </div>

            {formState.payerType && formState.payerType !== "-" && (
                <>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">
                                    Full Name
                                    {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                                </IonLabel>
                                <IonInput
                                    type="text"
                                    value={formState.name}
                                    aria-required={true}
                                    disabled={formState.payerType !== "other"}
                                    onIonChange={(e) => {
                                        handleNameChange(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                    </IonRow>
                </>
            )}

            {isLoading && (
                <IonRow>
                    <FormFieldsInlineSkeleton fieldCount={1} />
                </IonRow>
            )}
        </>
    );
};

export default SelectPayerSection;
